/**
 * SliderInput — range slider + number input combination kept in sync.
 * Single responsibility: bounded numeric value with slider UX.
 */

import React, { useEffect } from 'react';
import { NUMBER_INPUT_NO_NATIVE_SPIN_CLASS, ensureNoNativeSpinStyle } from './noNativeSpin';

export interface SliderInputProps {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  isDark: boolean;
}

/**
 * SliderInput component — label row above, then slider + number input side by side.
 */
export const SliderInput: React.FC<SliderInputProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  isDark,
}) => {
  useEffect(() => { ensureNoNativeSpinStyle(); }, []);

  const accent = isDark ? '#22c55e' : '#16a34a';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };

  const labelRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
  };

  const valueStyle: React.CSSProperties = {
    fontSize: 11,
    color: textSecondary,
    fontFamily: 'monospace',
  };

  const controlsRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  const sliderStyle: React.CSSProperties = {
    flex: 1,
    cursor: 'pointer',
    accentColor: accent,
    height: 4,
  };

  const numberInputStyle: React.CSSProperties = {
    width: 56,
    padding: '3px 5px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    textAlign: 'right',
    boxSizing: 'border-box',
    flexShrink: 0,
  };

  const stepperStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${borderColor}`,
  };

  const stepperBtnStyle: React.CSSProperties = {
    background: inputBg,
    border: 'none',
    color: textSecondary,
    cursor: 'pointer',
    padding: 0,
    width: 14,
    height: 11,
    fontSize: 8,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const unitStyle: React.CSSProperties = {
    fontSize: 11,
    color: textSecondary,
    flexShrink: 0,
    minWidth: 14,
  };

  const displayValue = value ?? min;

  const handleSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value));
  };

  const handleNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      onChange(undefined);
    } else {
      const n = parseFloat(raw);
      if (!isNaN(n)) onChange(Math.min(max, Math.max(min, n)));
    }
  };

  const bump = (delta: number) => {
    onChange(Math.min(max, Math.max(min, (value ?? min) + delta)));
  };

  return (
    <div style={containerStyle}>
      <div style={labelRowStyle}>
        <span style={labelStyle}>{label}</span>
        <span style={valueStyle}>
          {value !== undefined ? value : '—'}
          {unit && value !== undefined ? unit : ''}
        </span>
      </div>
      <div style={controlsRowStyle}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={displayValue}
          onChange={handleSlider}
          style={sliderStyle}
          aria-label={`${label} slider`}
        />
        <input
          type="number"
          className={NUMBER_INPUT_NO_NATIVE_SPIN_CLASS}
          min={min}
          max={max}
          step={step}
          value={value === undefined ? '' : value}
          onChange={handleNumber}
          style={numberInputStyle}
          aria-label={`${label} value`}
        />
        <span style={stepperStyle}>
          <button
            type="button"
            style={stepperBtnStyle}
            onClick={() => bump(step)}
            aria-label={`Increase ${label}`}
            title="Increase"
          >
            ▲
          </button>
          <button
            type="button"
            style={{ ...stepperBtnStyle, borderTop: `1px solid ${borderColor}` }}
            onClick={() => bump(-step)}
            aria-label={`Decrease ${label}`}
            title="Decrease"
          >
            ▼
          </button>
        </span>
        {unit && <span style={unitStyle}>{unit}</span>}
      </div>
    </div>
  );
};

SliderInput.displayName = 'SliderInput';
